/**
 * Design tokens. plan.md §2.
 *
 * A 1970s palette — warm, saturated, earthy. The two option sides are a fixed
 * pair, identical on every turn, so players learn them immediately.
 *
 * The pair differs by 33 L* (72.0 vs 38.8), so it survives any colour vision
 * deficiency: the sides read as light and dark before they read as gold and
 * rust. Text contrast clears AA on both — 8.5:1 dark-on-gold, 5.7:1
 * cream-on-rust. Each side also carries its own shape marker, so nothing ever
 * depends on colour alone.
 */

export const palette = {
  bg: "#17140F",
  surface: "#221D16",
  ink: "#F4EBDD",
  inkDim: "#A2968A",
  sideA: "#E8A33C",
  sideB: "#9E3B26",
  /**
   * Right and wrong, on the reveal only.
   *
   * Deliberately outside the side pair: gold and rust mean "option one" and
   * "option two" everywhere else in the game, so a correct guess tinted gold
   * would read as a side. Both clear AA on `bg`.
   */
  correct: "#8FBF5F",
  wrong: "#E0685A",
} as const;

/** Directional, matching the top/bottom split on a portrait phone. */
export enum SideMarker {
  Up = "up",
  Down = "down",
}

export enum OptionSideKey {
  A = "a",
  B = "b",
}

/**
 * The two option sides. Position, colour and shape all say the same thing, so
 * a player who cannot use one channel still has two others.
 */
export const SIDES = {
  [OptionSideKey.A]: {
    color: palette.sideA,
    ink: palette.bg,
    marker: SideMarker.Up,
  },
  [OptionSideKey.B]: {
    color: palette.sideB,
    ink: palette.ink,
    marker: SideMarker.Down,
  },
} as const;

/**
 * Player identity is a generated face — see AvatarFace. Players regenerate
 * freely at join and the chosen one is stored as a seed on their account, so
 * the avatar is reproduced rather than transferred.
 */

/**
 * Player name colours — vivid and deliberately outside the 1970s palette.
 *
 * Names have to be grabbed at a glance mid-conversation, so they get their own
 * bright, high-chroma set rather than the earthy game colours. They also stay
 * clear of gold and rust, which mean "option one" and "option two" everywhere
 * else — a name must never be mistaken for a side.
 */
export const NAME_COLORS = [
  "#B98BFF", // purple
  "#4FD1D9", // cyan
  "#F272B6", // pink
  "#8FE362", // lime
  "#6BA8F5", // blue
  "#F5D547", // yellow
  "#FF9166", // coral
  "#5FE3C0", // mint
] as const;

/** Timer bar shifts to this once the window is nearly gone. */
export const TIMER_URGENT_FRACTION = 0.25;

/** Minimum comfortable touch target. */
export const TAP_TARGET_PX = 44;
